/**
 * MobileOmnigateUse — Omnigate Use for Telegram Mini App.
 * Wzorzec: MobileAceStep. Komunikacja przez FastAPI proxy /v1/omnigate-use/*
 * Tabs: New Task | Tasks | Status
 */
import React, { useState, useEffect, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface MobileOmnigateUseProps {
  triggerHaptic: (style: 'light' | 'medium' | 'heavy') => void;
}

type Tab = 'new' | 'tasks' | 'status';

interface TaskInfo {
  task_id: string;
  prompt: string;
  status: string;
  profile: string;
  steps_total: number;
  steps_done: number;
  created_at: string;
}

interface TaskDetail {
  task_id: string;
  prompt: string;
  status: string;
  profile: string;
  plan: Array<{
    step_id: string;
    order: number;
    description: string;
    selected_model: string;
    status: string;
    tool_used: string;
  }>;
}

// ═══════════════════════════════════════════════════════════════
// STYLES (mobile-optimized, same palette as MobileAceStep)
// ═══════════════════════════════════════════════════════════════

const S: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex', flexDirection: 'column', height: '100%',
    background: 'linear-gradient(160deg, #0d0d1a 0%, #1a0d2e 50%, #0d1a2e 100%)',
    color: '#e8e8f8', fontFamily: "'Inter', -apple-system, sans-serif", overflow: 'hidden',
  },
  header: {
    padding: '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)',
    background: 'rgba(13,13,26,0.9)', backdropFilter: 'blur(16px)', flexShrink: 0,
  },
  headerTitle: { fontSize: 18, fontWeight: 700, margin: 0, color: '#fff' },
  headerSub: { fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 },
  tabs: {
    display: 'flex', gap: 6, marginTop: 12,
    background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3,
  },
  tabBtn: (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '8px 6px', borderRadius: 8, border: 'none',
    background: active ? 'linear-gradient(135deg, #7c3aed, #2563eb)' : 'transparent',
    color: active ? '#fff' : 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: active ? 600 : 400,
    cursor: 'pointer', transition: 'all 0.2s ease',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  }),
  content: { flex: 1, overflow: 'auto', padding: '16px 20px' },
  input: {
    width: '100%', minHeight: 100, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: '#e8e8f8', fontSize: 14, padding: 14,
    resize: 'vertical' as const, outline: 'none', marginBottom: 12,
  },
  profileBtn: (active: boolean): React.CSSProperties => ({
    padding: '8px 14px', borderRadius: 20, border: '1px solid',
    borderColor: active ? '#7c3aed' : 'rgba(255,255,255,0.1)',
    background: active ? 'rgba(124,58,237,0.15)' : 'transparent',
    color: active ? '#c4b5fd' : 'rgba(255,255,255,0.5)',
    fontSize: 11, fontWeight: 600, cursor: 'pointer', marginRight: 6, marginBottom: 6,
  }),
  generateBtn: (disabled: boolean): React.CSSProperties => ({
    width: '100%', padding: '14px', borderRadius: 12, border: 'none',
    background: disabled ? 'rgba(124,58,237,0.3)' : 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
    color: disabled ? 'rgba(255,255,255,0.4)' : '#fff', fontSize: 15, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', marginTop: 8,
    boxShadow: disabled ? 'none' : '0 4px 20px rgba(124,58,237,0.4)',
  }),
  taskCard: {
    background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 14, marginBottom: 10,
    border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer',
  },
  statusBadge: (status: string): React.CSSProperties => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700,
    background: status === 'completed' ? 'rgba(34,197,94,0.15)' :
                status === 'executing' ? 'rgba(59,130,246,0.15)' :
                status === 'failed' ? 'rgba(239,68,68,0.15)' :
                status === 'awaiting_approval' ? 'rgba(234,179,8,0.15)' : 'rgba(255,255,255,0.05)',
    color: status === 'completed' ? '#4ade80' :
           status === 'executing' ? '#60a5fa' :
           status === 'failed' ? '#f87171' :
           status === 'awaiting_approval' ? '#facc15' : 'rgba(255,255,255,0.5)',
  }),
  stepItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

const PROFILES = ['balanced', 'safe', 'research', 'checklist', 'yolo', 'custom'];
const PROFILE_EMOJI: Record<string, string> = {
  balanced: '⚖️', safe: '🛡️', research: '🔍', checklist: '✅', yolo: '🚀', custom: '⚙️'
};

const MobileOmnigateUse: React.FC<MobileOmnigateUseProps> = ({ triggerHaptic }) => {
  const [tab, setTab] = useState<Tab>('new');
  const [prompt, setPrompt] = useState('');
  const [profile, setProfile] = useState('balanced');
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<TaskInfo[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [message, setMessage] = useState('');
  const apiKey = () => localStorage.getItem('omnigate_api_key') || '';
  const authHeaders = () => ({ 'X-API-Key': apiKey(), 'Content-Type': 'application/json' as const });

  const fetchTasks = useCallback(async () => {
    try {
      const r = await fetch('/v1/omnigate-use/tasks', { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setTasks(data.tasks || []);
      }
    } catch {}
  }, []);

  const fetchTaskDetail = async (taskId: string) => {
    try {
      const r = await fetch(`/v1/omnigate-use/task/${taskId}`, { headers: authHeaders() });
      if (r.ok) setSelectedTask(await r.json());
    } catch {}
  };

  useEffect(() => { if (tab === 'tasks') fetchTasks(); }, [tab]);

  const handleCreate = async () => {
    if (!prompt.trim()) return;
    triggerHaptic('medium');
    setLoading(true);
    setMessage('Tworzenie zadania...');
    try {
      const r = await fetch('/v1/omnigate-use/task/create', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ prompt, profile })
      });
      const data = await r.json();
      if (data.task_id) {
        setMessage(`✅ Zadanie ${data.task_id} utworzone!`);
        setPrompt('');
        setTab('tasks');
      } else {
        setMessage(`❌ ${data.error || 'Błąd'}`);
      }
    } catch (e: any) {
      setMessage(`❌ ${e.message}`);
    }
    setLoading(false);
  };

  const handleApprove = async (taskId: string) => {
    triggerHaptic('medium');
    await fetch(`/v1/omnigate-use/task/${taskId}/approve`, { method: 'POST', headers: authHeaders() });
    fetchTasks();
    if (selectedTask?.task_id === taskId) fetchTaskDetail(taskId);
  };

  const handleCancel = async (taskId: string) => {
    triggerHaptic('light');
    await fetch(`/v1/omnigate-use/task/${taskId}/cancel`, { method: 'POST', headers: authHeaders() });
    fetchTasks();
  };

  const handleStepRetry = async (taskId: string, stepId: string) => {
    await fetch(`/v1/omnigate-use/task/${taskId}/step/${stepId}/retry`, { method: 'POST', headers: authHeaders() });
    fetchTaskDetail(taskId);
  };

  return (
    <div style={S.root}>
      {/* Header */}
      <div style={S.header}>
        <h1 style={S.headerTitle}>🧠 Omnigate Use</h1>
        <p style={S.headerSub}>Planowanie · Approval · Wykonanie</p>
        <div style={S.tabs}>
          {(['new', 'tasks', 'status'] as Tab[]).map(t => (
            <button key={t} style={S.tabBtn(tab === t)} onClick={() => { setTab(t); triggerHaptic('light'); }}>
              {t === 'new' ? '✨ Nowe' : t === 'tasks' ? '📋 Zadania' : '📊 Status'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={S.content}>
        {/* NEW TASK TAB */}
        {tab === 'new' && (
          <>
            <textarea
              style={S.input}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Opisz zadanie dla agenta AI..."
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
              {PROFILES.map(p => (
                <button key={p} style={S.profileBtn(profile === p)} onClick={() => { setProfile(p); triggerHaptic('light'); }}>
                  {PROFILE_EMOJI[p]} {p}
                </button>
              ))}
            </div>
            <button style={S.generateBtn(loading || !prompt.trim())} onClick={handleCreate} disabled={loading || !prompt.trim()}>
              {loading ? '⏳ Tworzenie...' : '🚀 Execute Task'}
            </button>
            {message && <p style={{ fontSize: 12, marginTop: 10, color: message.startsWith('✅') ? '#4ade80' : '#f87171' }}>{message}</p>}
          </>
        )}

        {/* TASKS TAB */}
        {tab === 'tasks' && (
          <>
            {selectedTask ? (
              <>
                <button onClick={() => setSelectedTask(null)} style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                  ← Powrót do listy
                </button>
                <div style={{ marginBottom: 12 }}>
                  <span style={S.statusBadge(selectedTask.status)}>{selectedTask.status}</span>
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{selectedTask.profile}</span>
                </div>
                <p style={{ fontSize: 13, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>{selectedTask.prompt}</p>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#7c3aed' }}>Plan:</div>
                {selectedTask.plan?.map(step => (
                  <div key={step.step_id} style={S.stepItem}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', width: 24 }}>{step.order}.</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12 }}>{step.description}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{step.tool_used} · {step.selected_model}</div>
                    </div>
                    <span style={S.statusBadge(step.status)}>{step.status}</span>
                    {step.status === 'failed' && (
                      <button onClick={() => handleStepRetry(selectedTask.task_id, step.step_id)}
                        style={{ background: 'rgba(234,179,8,0.2)', border: 'none', color: '#facc15', borderRadius: 14, padding: '4px 10px', fontSize: 10, cursor: 'pointer' }}>
                        Retry
                      </button>
                    )}
                  </div>
                ))}
                {selectedTask.status === 'awaiting_approval' && (
                  <button onClick={() => handleApprove(selectedTask.task_id)}
                    style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: '#22c55e', color: '#fff', fontSize: 13, fontWeight: 700, marginTop: 16, cursor: 'pointer' }}>
                    ✅ Approve & Execute
                  </button>
                )}
              </>
            ) : (
              <>
                {tasks.length === 0 && <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Brak zadań</p>}
                {tasks.map(t => (
                  <div key={t.task_id} style={S.taskCard} onClick={() => fetchTaskDetail(t.task_id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={S.statusBadge(t.status)}>{t.status}</span>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{t.steps_done}/{t.steps_total}</span>
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>{t.prompt.slice(0, 80)}{t.prompt.length > 80 ? '...' : ''}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{t.profile} · {t.task_id.slice(0, 8)}</div>
                  </div>
                ))}
                <button onClick={fetchTasks} style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#fff', fontSize: 12, marginTop: 12, cursor: 'pointer' }}>
                  🔄 Odśwież
                </button>
              </>
            )}
          </>
        )}

        {/* STATUS TAB */}
        {tab === 'status' && (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🧠</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Omnigate Use</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
              Agent AI z dostępem do wszystkich narzędzi Omnigate
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 16, textAlign: 'left' }}>
              <div style={S.stepItem}><span>🖥️</span><span style={{ fontSize: 12 }}>Pełny dostęp do systemu</span></div>
              <div style={S.stepItem}><span>🌐</span><span style={{ fontSize: 12 }}>Browser agent + VNC</span></div>
              <div style={S.stepItem}><span>🛠️</span><span style={{ fontSize: 12 }}>47+ narzędzi MCP</span></div>
              <div style={S.stepItem}><span>🧠</span><span style={{ fontSize: 12 }}>10+ modeli AI</span></div>
              <div style={S.stepItem}><span>📱</span><span style={{ fontSize: 12 }}>Telegram Mobile</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileOmnigateUse;
